/**
 * 视频URL处理工具函数
 */

import { getVideoCDNPrefix, getVideoStoragePrefix } from '../config/video';

/**
 * 处理视频URL，为相对路径添加代理前缀
 * @param url 原始URL
 * @param useCache 是否使用缓存路径（默认 false，使用 /storage）
 *                 true: 使用 /media（内容哈希资源，可强缓存）
 *                 false: 使用 /storage（非缓存资源）
 * @returns 处理后的完整URL
 */
export const processVideoUrl = (url: string | null | undefined, useCache: boolean = false): string => {
  if (!url) {
    return '';
  }

  // 如果URL已经是完整的HTTP/HTTPS链接，直接返回
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // ✅ 新增：如果URL已经包含代理前缀（/storage 或 /media），直接返回，避免重复拼接
  if (url.startsWith('/storage/') || url.startsWith('/media/')) {
    return url;
  }

  const prefix = useCache ? getVideoCDNPrefix() : getVideoStoragePrefix();
  
  // 如果前缀是相对路径（如 /media 或 /storage），需要确保路径拼接正确
  if (prefix.startsWith('/')) {
    // 前缀是相对路径，确保 url 以 / 开头
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return `${prefix}${cleanUrl}`;
  } else {
    // 前缀是绝对路径（兼容旧配置），去掉 url 开头的 /，然后拼接
    const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
    return `${prefix}/${cleanUrl}`;
  }
};

/**
 * 处理目标台词音频URL（trans_audio）
 * 目标台词音频使用内容哈希命名，可以使用强缓存
 * @param url 原始URL
 * @returns 处理后的完整URL（使用 /media 路径）
 */
export const processTransAudioUrl = (url: string | null | undefined): string => {
  return processVideoUrl(url, true); // 使用缓存路径
};

/**
 * 获取视频播放URL，按优先级处理
 * @param item 视频数据项
 * @returns 处理后的视频URL
 */
export const getVideoPlayUrl = (item: any): string => {
  // 按优先级获取URL：result_url > pure_video_url > file_url
  const rawUrl = item.result_url || item.pure_video_url || item.file_url;
  
  if (!rawUrl) {
    return '';
  }

  return processVideoUrl(rawUrl);
};

/**
 * 检查URL是否为有效的视频链接
 * @param url 视频URL
 * @returns 是否为有效链接
 */
export const isValidVideoUrl = (url: string): boolean => {
  return Boolean(url && url.length > 0 && (url.startsWith('http://') || url.startsWith('https://')));
};
